import {
  EmptyState,
  EmptyStateContainer,
} from 'module/ui/components/atoms/empty-state';
import { faFileText } from '@fortawesome/pro-solid-svg-icons';
import { Icon, Loader } from '@rocketlane/ui';
import {
  MeetingTranscript as MeetingTranscriptType,
  SignalOccurrenceWithTimePeriod,
} from 'api/meeting/types';
import { useMeetingTranscriptQuery } from 'queries/meeting';
import { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { JumpToCurrentTime } from './jump-to-current-time';
import { useVideoPlayerContext } from './player-context';
import { SignalNavigator } from './signal-navigator';
import { useSyncTranscriptWithPlayer } from './sync-transcript-with-player';
import { Transcript } from './transcript';
import { useDetectUserScroll } from './user-scroll-detector';

type MeetingTranscriptContentProps = {
  transcript: MeetingTranscriptType;
  signalOccurrencesForMeetingWithTimePeriod: SignalOccurrenceWithTimePeriod[];
};

const MeetingTranscriptContent = ({
  transcript,
  signalOccurrencesForMeetingWithTimePeriod,
}: MeetingTranscriptContentProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { canPlay } = useVideoPlayerContext();
  const { isAutoScrollEnabled, startProgrammaticScroll, enableAutoScroll } =
    useDetectUserScroll(scrollContainerRef);
  const { activeSegment, activeChunk, scrollToSegment } =
    useSyncTranscriptWithPlayer({
      transcript,
      isAutoScrollEnabled: canPlay && isAutoScrollEnabled,
      beforeScrollToSegment: startProgrammaticScroll,
    });
  const enableAutoScrollToSegment = useCallback(() => {
    enableAutoScroll();
    if (activeSegment) {
      scrollToSegment(activeSegment);
    } else if (transcript != null) {
      scrollToSegment(transcript.segments[0]);
    }
  }, [activeSegment, enableAutoScroll, scrollToSegment, transcript]);

  return (
    <div className="h-full w-full relative overflow-hidden">
      <SignalNavigator
        signalOccurrencesForMeetingWithTimePeriod={
          signalOccurrencesForMeetingWithTimePeriod
        }
        triggerClassName="absolute top-1/2 -translate-y-1/2 left-[32px]"
      />
      <div
        ref={scrollContainerRef}
        className="py-05 pl-09 overflow-y-auto h-full"
      >
        <div className="w-[768px] mx-auto">
          <Transcript
            activeSegment={activeSegment}
            activeChunk={activeChunk}
            transcript={transcript}
            signalOccurrencesForMeetingWithTimePeriod={
              signalOccurrencesForMeetingWithTimePeriod
            }
          />
        </div>
      </div>
      {canPlay && (
        <JumpToCurrentTime
          isAutoScrollEnabled={isAutoScrollEnabled}
          enableAutoScrollToSegment={enableAutoScrollToSegment}
        />
      )}
    </div>
  );
};

const signalOccurrencesForMeetingWithTimePeriod = [];

type MeetingTranscriptProps = {
  meetingId: number;
};
export const MeetingTranscript = ({ meetingId }: MeetingTranscriptProps) => {
  const { data: transcript, isLoading: isMeetingTranscriptLoading } =
    useMeetingTranscriptQuery(meetingId);

  return isMeetingTranscriptLoading ? (
    <div className="flex justify-center items-center h-full">
      <Loader size={24} />
    </div>
  ) : transcript == null ? (
    <div className="flex pl-09 h-full overflow-hidden">
      <div className="w-[768px] mx-auto">
        <EmptyStateContainer>
          <EmptyState
            image={<Icon icon={faFileText} size={'2x'} />}
            message={
              <FormattedMessage id="account.meeting_transcript.no_transcript" />
            }
          />
        </EmptyStateContainer>
      </div>
    </div>
  ) : (
    <MeetingTranscriptContent
      transcript={transcript}
      signalOccurrencesForMeetingWithTimePeriod={
        signalOccurrencesForMeetingWithTimePeriod
      }
    />
  );
};
